import { type Ref } from 'vue';
import type { useWorkspaceStore } from '../stores/workspace';
import type { EntityDocument } from '../types/entities';
import type { TimelineOccurrence, TimelineOccurrenceDraft } from '../types/timeline';

export function useTimelineDraftActions(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  isVersionReadonly: Readonly<Ref<boolean>>;
  timelineDraft: TimelineOccurrenceDraft;
  timelineState: Ref<'idle' | 'saving' | 'error'>;
  timelineError: Ref<string>;
  inheritedChapterOccurrence: Readonly<Ref<TimelineOccurrence | null>>;
  hydrateTimelineDraft: () => void;
}) {
  const { store, activeEntity, isVersionReadonly, timelineDraft, timelineState, timelineError, inheritedChapterOccurrence, hydrateTimelineDraft } = options;
async function saveTimeline() {
  if (!activeEntity.value || isVersionReadonly.value) return;
  timelineState.value = 'saving'; timelineError.value = '';
  try {
    await store.saveTimelineOccurrence(activeEntity.value.id, {
      eraEntityId: timelineDraft.eraEntityId, inUniverseStart: timelineDraft.inUniverseStart.trim() || null,
      inUniverseEnd: timelineDraft.inUniverseEnd.trim() || null, sortKey: Number(timelineDraft.sortKey) || 0,
      narrativePosition: timelineDraft.narrativePosition === null || timelineDraft.narrativePosition === undefined ? null : Number(timelineDraft.narrativePosition),
      precision: timelineDraft.precision, isUncertain: timelineDraft.isUncertain, notes: timelineDraft.notes.trim() || null
      , calendarYear: timelineDraft.calendarYear === null || timelineDraft.calendarYear === undefined ? null : Number(timelineDraft.calendarYear), calendarPeriod: timelineDraft.calendarPeriod === null || timelineDraft.calendarPeriod === undefined ? null : Number(timelineDraft.calendarPeriod), calendarDay: timelineDraft.calendarDay === null || timelineDraft.calendarDay === undefined ? null : Number(timelineDraft.calendarDay), calendarParts: { ...timelineDraft.calendarParts }, clockLabel: timelineDraft.clockLabel.trim() || null, timeBasis: timelineDraft.timeBasis, absoluteTime: timelineDraft.absoluteTime, timeSource: timelineDraft.timeSource, narrativeRelation: timelineDraft.narrativeRelation, relativeAnchor: timelineDraft.relativeAnchor, relativeOffsetMinutes: timelineDraft.relativeOffsetMinutes === null || timelineDraft.relativeOffsetMinutes === undefined ? null : Number(timelineDraft.relativeOffsetMinutes)
    });
    timelineState.value = 'idle';
  } catch (cause) { timelineState.value = 'error'; timelineError.value = cause instanceof Error ? cause.message : 'Timeline konnte nicht gespeichert werden.'; }
}
function prepareSceneExplicitTime() {
  if (timelineDraft.timeSource === 'relative' && !timelineDraft.relativeAnchor) timelineDraft.relativeAnchor = 'chapter-start';
  if (timelineDraft.timeSource !== 'explicit') return;
  const hasOwnTime = Boolean(timelineDraft.absoluteTime || Object.values(timelineDraft.calendarParts).some((value) => value !== null && value !== ''));
  const inherited = inheritedChapterOccurrence.value;
  if (hasOwnTime || !inherited) return;
  Object.assign(timelineDraft, {
    calendarYear: inherited.calendarYear, calendarPeriod: inherited.calendarPeriod, calendarDay: inherited.calendarDay,
    calendarParts: { ...inherited.calendarParts }, clockLabel: inherited.clockLabel ?? '', timeBasis: inherited.timeBasis ?? 'calendar', absoluteTime: inherited.absoluteTime
  });
}
async function removeTimeline() {
  if (!activeEntity.value || isVersionReadonly.value) return;
  await store.deleteTimelineOccurrence(activeEntity.value.id);
  hydrateTimelineDraft();
}
  return { saveTimeline, prepareSceneExplicitTime, removeTimeline };
}
